import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import api from '../../services/api';
import { rackPieces, SavedMerchandisingPlan } from './merchandisingTypes';
import { readNokeError } from './restockPlan';
import RackCard from './RackCard';

const MerchandisingHistory: React.FC = () => {
  const [plans, setPlans] = useState<SavedMerchandisingPlan[]>([]);
  const [selected, setSelected] = useState('');
  const [saved, setSaved] = useState<SavedMerchandisingPlan | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  useEffect(() => {
    let active = true;
    api.get('/api/noke/merchandising/plans', { params: { limit: 30 } }).then(({ data }) => {
      if (!active) return;
      setPlans(data.plans); setNext(data.next_before_id ?? null);
      setSelected(data.plans[0] ? String(data.plans[0].id) : '');
    }).catch((err) => { if (active) setError(readNokeError(err, '無法讀取歷史配置。')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!selected) return;
    const id = ++request.current;
    setSaved(null); setError(''); setLoading(true);
    api.get(`/api/noke/merchandising/plans/${selected}`).then(({ data }) => {
      if (request.current === id) setSaved(data.plan);
    }).catch((err) => { if (request.current === id) setError(readNokeError(err, '無法讀取這版配置。')); })
      .finally(() => { if (request.current === id) setLoading(false); });
    return () => { request.current += 1; };
  }, [selected]);
  const loadMore = async () => {
    if (!next || loadingMore) return;
    setLoadingMore(true); setError('');
    try {
      const { data } = await api.get('/api/noke/merchandising/plans', { params: { limit: 30, before_id: next } });
      setPlans((old) => [...old, ...data.plans.filter((row: SavedMerchandisingPlan) => !old.some((existing) => existing.id === row.id))]);
      setNext(data.next_before_id ?? null);
    } catch (err) { setError(readNokeError(err, '無法讀取更早的配置。')); }
    finally { setLoadingMore(false); }
  };
  return <Box sx={{ py: 2 }}>
    <Alert severity="info" sx={{ mb: 2 }}>歷史配置僅供查看。件數、缺口與選品依據均為儲存當時的紀錄；不是目前庫存，也不會從這裡叫貨或登記上架。</Alert>
    {plans.length > 0 && <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
      <TextField select label="配置日期與版本" value={selected} onChange={(event) => setSelected(event.target.value)} size="small" sx={{ minWidth: 250 }}>
        {plans.map((row) => <MenuItem key={row.id} value={String(row.id)}>{row.label || `${row.date_local} · 版本 ${row.id}`}{row.active ? '（目前採用）' : ''}</MenuItem>)}
      </TextField>
      {next && <Button disabled={loadingMore} onClick={loadMore}>載入更早配置</Button>}
    </Box>}
    {loading && <Box role="status"><CircularProgress size={22} /> 正在讀取歷史配置…</Box>}
    {error && <Alert severity="error">{error}</Alert>}
    {!loading && !error && plans.length === 0 && <Typography>尚未儲存任何配置。</Typography>}
    {saved?.payload && <>
      <Typography variant="body2" sx={{ mb: 2 }}>儲存於 {new Date(saved.created_at).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })} · {new Set(saved.payload.racks.flatMap((rack) => rack.items.map((item) => item.sku))).size} 款／{saved.payload.racks.reduce((sum, rack) => sum + rackPieces(rack), 0)} 件</Typography>
      {saved.payload.planning_horizon && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>當時預計 {saved.payload.planning_horizon.arrival_start}–{saved.payload.planning_horizon.arrival_end} 到貨，規劃販售至 {saved.payload.planning_horizon.selling_end}。</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        {saved.payload.racks.map((rack) => <RackCard key={rack.id} rack={rack} enabled={false} historical onPlace={() => {}} onFit={() => {}} />)}
      </Box>
    </>}
  </Box>;
};

export default MerchandisingHistory;
